import { Component, DOCUMENT, Inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { UpperCasePipe, CommonModule } from '@angular/common';
import { LoadingComponent } from '../../components/loading/loading.component';
import { NotFoundComponent } from '../../components/not-found/not-found.component';
import { Bind } from 'primeng/bind';
import { ChipModule } from 'primeng/chip';
import { CountdownDirective } from '../../directives/countdown.directive';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ApiService } from '../../_utils/api/api.service';
import { slugify } from '../../_utils/functions';
import { InsightService } from '../../_utils/insight/insight.service';
import { SeoService } from '../../_utils/seo/seo.service';
import { GalleriaModule } from 'primeng/galleria';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-offer',
    templateUrl: './offer.component.html',
    styleUrl: './offer.component.scss',
    imports: [CommonModule, GalleriaModule, LoadingComponent, NotFoundComponent, TranslocoDirective, Bind, ChipModule, CountdownDirective, ButtonModule, UpperCasePipe, TranslatePipe],
    encapsulation: ViewEncapsulation.None
})
export class OfferComponent implements OnInit {
  id: string = "";
  error: string = "";
  loading = signal(false);
  galleryActiveIndex: number = 0;
  currentLang: string = 'it';
  offer: any = null;
  data: any = null;
  private subscriptions = new Subscription();

  constructor(@Inject(DOCUMENT) private document: Document, protected translocoService: TranslocoService, protected insightService: InsightService, private seoService: SeoService, private titleService: Title, private metaTagService: Meta, private route: ActivatedRoute, protected router: Router, private apiService: ApiService) { }

  ngOnInit(): void {
    this.loading.set(true);
    this.currentLang = this.translocoService.getActiveLang();

    this.subscriptions.add(
      this.translocoService.langChanges$.subscribe((lang) => {
        this.currentLang = lang;
      })
    );

    this.subscriptions.add(this.route.params.subscribe((params) => {
      if (params['id']) {
        this.id = params['id'];
        this.apiService.getCompany().subscribe({
          next: (company) => {
            this.loading.set(false);
            if (typeof window === 'undefined' && params['id'].includes('css')) //check for other things (css map)
              return;
              
            this.offer = (company.data.offers)?.find((offer: any) => offer.id == this.id);

            const translatePipe = new TranslatePipe(this.translocoService);

            if (!this.offer && company.data.vanity_url) {
              this.offer = (company.data.offers)?.find((offer: any) => {
                return Object.values(offer.titolo).some((val: any) => slugify(val) == params['id']);
              });
            }
            
            let offerDescription = this.offer?.descrizione ? translatePipe.transform(company.data.description) : null;
            let description = translatePipe.transform(company.data.description);
            let title = this.offer?.titolo ? translatePipe.transform(this.offer?.titolo) : null;
            this.seoService.setSeo({
              title: title || company.data.denominazione,
              description: offerDescription || description,
              image: this.offer?.foto || company.data.image || company.data.logo,
              url: company.data.pwa_url + '/proposta/' + params['id']
            });

            if (typeof window === 'undefined')
              return;

            this.data = company.data;
            this.insightService.send({
              event: 'open_page',
              event_name: 'proposta'
            });
          },
          error: (error) => {
            console.error(error);
            this.router.navigate(['error'], { queryParams: { message: error } });
          }
        });
      } else {
        console.error('Please provide a valid id');
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Format the price to show the decimal part with a comma
   * @param price string price
   * @returns a new price string
   */
  formatPrice(price: number): string {
    return price.toFixed(2).toString().replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  rawurlencode(str: string) {
    // Convert the string to UTF-8 encoding
    str = str.toString();
    // Encode all characters except the following: alphabetic, decimal digits, - _ . ! ~ * ' ( )
    return encodeURIComponent(str).replace(/!/g, '%21')
      .replace(/'/g, '%27')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29')
      .replace(/\*/g, '%2A');
  }

  getLocationTitle(id: string): string {
    const location = this.data.locations?.find((item: any) => item.id == id);
    const title = location?.title;

    if (!title) {
      return '';
    }

    if (typeof title === 'string') {
      return title;
    }

    return title[this.currentLang] || title.it || Object.values(title)[0] || '';
  }

  generateShareOfferUrl(offer: any): string {
    const translatePipe = new TranslatePipe(this.translocoService);
    return this.data.pwa_url + '/proposta/' + (this.data.vanity_url ? slugify(translatePipe.transform(offer.titolo)) : offer.id);
  }

  goToOffersByTag(tag: string): void {
    const normalizedTag = (tag || '').trim();
    if (!normalizedTag)
      return;

    this.router.navigate(['/proposte'], {
      queryParams: {
        tags: normalizedTag
      }
    });
  }

  /**
   * Get the image path for a file based on its extension
   * @param extension file extension
   * @returns image path
   */
  getFileImage(extension: string): string {
    if (!extension) return '/files/file.png';
    
    const ext = extension.toLowerCase();
    const fileImages: { [key: string]: string } = {
      'pdf': '/files/pdf-file.png',
      'doc': '/files/doc-file.png',
      'docx': '/files/docx-file.png',
      'xls': '/files/xls-file.png',
      'xlsx': '/files/xls-file.png',
      'jpg': '/files/jpg-file.png',
      'png': '/files/png-file.png',
      'txt': '/files/txt-file.png',
      'svg': '/files/svg-file.png',
    };
    
    return fileImages[ext] || '/files/file.png';
  }


  canBrowserShareData(): boolean {
    // Avoid hydration mismatch: only run in browser
    if (typeof navigator === 'undefined' || !navigator.share || !navigator.canShare) {
      return false;
    }

    return navigator.canShare({ url: this.document.location.href, text: this.offer?.titolo });
  }

  share(): void {
    if (!navigator.share) {
      console.error('Web Share API is not supported in this browser.');
      return;
    }

    const translatePipe = new TranslatePipe(this.translocoService);

    navigator
      .share({
        title: translatePipe.transform(this.offer?.titolo),
        text: translatePipe.transform(this.offer?.titolo),
        url: this.document.location.href,
      })
      .then(() => {
        console.log('Content shared successfully');
        this.insightService.send({ event: 'click', event_name: 'share' });
      })
      .catch((error) => console.error('Error sharing content:', error));
  }
}
